import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { CreatedUser, OkResponse, UserDto } from "@reina/shared";
import type { ChangePasswordInput, CreateUserInput, User } from "@reina/shared";
import { api } from "./client";

const UsersList = z.object({ items: z.array(UserDto) });
const UserEnvelope = z.object({ user: UserDto });

export const usersKey = ["users"] as const;

export function useUsers() {
  return useQuery({
    queryKey: usersKey,
    queryFn: async (): Promise<User[]> => (await api.get("/users", { schema: UsersList })).items,
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateUserInput) => api.post("/users", input, { schema: CreatedUser }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: usersKey }),
  });
}

export function useSetUserActive() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.patch(`/users/${id}/active`, { isActive }, { schema: UserEnvelope }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: usersKey }),
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (input: ChangePasswordInput) =>
      api.post("/auth/change-password", input, { schema: OkResponse }),
  });
}
